import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FONT, COLOR } from "../lib/tokens";

// OnboardingTour — swipeable "here's the app" chapters shown once,
// right after onboarding completes (stage "tour" of the
// booda:postOnboarding sequencer in AuthedApp). Chapter SETS are
// persona-driven: a keep-it-simple cook gets two screens; a stocked-
// kitchen family gets the full five. Copy lives in data so persona
// variants stay declarative. Skippable at every moment.
//
// Art: public/img hero set (same images the CreateMenu cards use) —
// swap files in place to re-skin without touching code.

const CHAPTERS = {
  stock: {
    title: "Stock your kitchen",
    blurb: "Scan a barcode, snap a receipt, or just type it in — Booda keeps track of what's on hand, what's running low, and what's about to expire.",
    img: "/img/stocking-pantry-hero.webp",
  },
  generate: {
    title: "Recipes from what you have",
    blurb: "Tap the + button and ask for anything. Booda drafts a real recipe around your kitchen, your diet, and your allergies — every time, made for you.",
    img: "/img/ai-cooking-hero.webp",
  },
  generateExplorer: {
    title: "Any dish. Any cuisine.",
    blurb: "Tap the + button and dream out loud — new cuisines, wild fusions, week-long themes. Booda drafts real recipes for all of it, and your book keeps growing.",
    img: "/img/ai-cooking-hero.webp",
  },
  cook: {
    title: "Cook it step by step",
    blurb: "Cook Mode walks you through with timers, swaps, and a lock-screen card. Family can jump in and take a step — many hands, one dinner.",
    img: "/img/cooking-hero.webp",
  },
  plan: {
    title: "Plan the week, shop once",
    blurb: "Schedule meals on the calendar and build the shopping list from what's missing. Shop Mode checks things off as they land in your cart.",
    img: "/img/add-to-shopping-cart-hero.webp",
  },
  family: {
    title: "Cook together",
    blurb: "Invite family and friends — share recipes, review each other's cooks, and let the kids explore in their own kid-safe corner.",
    img: "/img/add-to-kitchen-hero.webp",
  },
};

// Persona → chapter ids. Simple cooks get the two-beat version;
// everyone else scales up. null (skipped persona) = full set.
const CHAPTER_SETS = {
  simple:   ["generate", "cook"],
  dayof:    ["generate", "plan", "cook"],
  explorer: ["generateExplorer", "cook", "plan", "family"],
  stocker:  ["stock", "generate", "cook", "plan", "family"],
};

export default function OnboardingTour({ cookStyle, onDone }) {
  const ids = CHAPTER_SETS[cookStyle] || CHAPTER_SETS.stocker;
  const chapters = ids.map((id) => ({ id, ...CHAPTERS[id] }));
  const [idx, setIdx] = useState(0);
  const last = idx === chapters.length - 1;
  const ch = chapters[idx];

  const next = () => (last ? onDone() : setIdx(idx + 1));
  const prev = () => setIdx(Math.max(0, idx - 1));

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 440,
      background: COLOR.night,
      display: "flex", flexDirection: "column",
      maxWidth: 480, margin: "0 auto",
      padding: "calc(env(safe-area-inset-top, 0px) + 16px) 24px calc(env(safe-area-inset-bottom, 0px) + 24px)",
    }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={onDone}
          style={{
            background: "none", border: "none", padding: "10px 6px",
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
            color: COLOR.muted, letterSpacing: "0.1em", cursor: "pointer",
          }}
        >
          SKIP
        </button>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={ch.id}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragEnd={(_, info) => {
            if (info.offset.x < -60) next();
            else if (info.offset.x > 60) prev();
          }}
          style={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            textAlign: "center", cursor: "grab",
          }}
        >
          <img
            src={ch.img}
            alt=""
            aria-hidden
            draggable={false}
            style={{
              width: "min(76%, 300px)", height: "auto",
              marginBottom: 32, userSelect: "none",
              filter: "drop-shadow(0 14px 30px rgba(30,20,8,0.18))",
            }}
          />
          <h2 style={{
            fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
            fontSize: 30, color: COLOR.ink, lineHeight: 1.15,
            letterSpacing: "-0.01em", margin: "0 0 14px", maxWidth: 320,
          }}>
            {ch.title}
          </h2>
          <p style={{
            fontFamily: FONT.forma, fontSize: 15, color: COLOR.muted,
            lineHeight: 1.6, maxWidth: 320, margin: 0,
          }}>
            {ch.blurb}
          </p>
        </motion.div>
      </AnimatePresence>

      <div style={{ display: "flex", gap: 7, justifyContent: "center", margin: "18px 0" }}>
        {chapters.map((c, i) => (
          <div key={c.id} style={{
            width: i === idx ? 22 : 7, height: 7, borderRadius: 4,
            background: i === idx ? COLOR.gold : COLOR.border,
            transition: "all 0.3s ease",
          }} />
        ))}
      </div>

      <button
        onClick={next}
        className="mcm-focusable"
        style={{
          width: "100%", padding: "16px", borderRadius: 14, border: "none",
          background: COLOR.gold, color: COLOR.night,
          fontFamily: FONT.forma, fontSize: 14, fontWeight: 700,
          letterSpacing: "0.08em", cursor: "pointer",
        }}
      >
        {last ? "START COOKING" : "NEXT"}
      </button>
    </div>,
    document.body,
  );
}
